import { useState, useEffect } from 'react'
import type { Panel, TestType, PanelExtraAnalyte } from '@alpha-edge/shared'
import {
  Modal,
  FormField,
  btnPrimary,
  btnSecondary,
  btnDanger,
  colors,
  input,
  ConfirmDialog,
} from '../../components/ui'
import { useModal } from '../../hooks/use-modal'
import { api, ApiError } from '../../services/api'

// PLAN.md §7.6.24 Slice 3 -- per-member config, keyed by testTypeId. Absent
// entry (or absent map) means required + included, matching the backend's
// "absent = required + included" default (packages/api-server/src/db/schema/panels.ts).
interface MemberConfigEntry {
  required: boolean
  includeInReport: boolean
}

const defaultMemberConfig: MemberConfigEntry = { required: true, includeInReport: true }

interface PanelForm {
  code: string
  name: string
  description: string
  testTypeIds: readonly string[]
  isReflexive: boolean
  reflexRulesJson: string
  memberConfig: Record<string, MemberConfigEntry>
}

const defaults: PanelForm = {
  code: '',
  name: '',
  description: '',
  testTypeIds: [],
  isReflexive: false,
  reflexRulesJson: '[]',
  memberConfig: {},
}

interface PanelEditModalProps {
  readonly panelId: string | null
  readonly isOpen: boolean
  readonly isNew: boolean
  readonly onClose: () => void
  readonly onSaved: (panel: Panel) => void
  readonly onArchived?: (panelId: string) => void
}

function panelToForm(p: Panel): PanelForm {
  return {
    code: p.code,
    name: p.name,
    description: p.description ?? '',
    testTypeIds: p.testTypeIds,
    isReflexive: p.isReflexive,
    reflexRulesJson: JSON.stringify(p.reflexRules, null, 2),
    memberConfig: p.memberConfig ?? {},
  }
}

export function PanelEditModal({ panelId, isOpen, isNew, onClose, onSaved, onArchived }: PanelEditModalProps) {
  const [form, setForm] = useState<PanelForm>(defaults)
  const [testTypes, setTestTypes] = useState<readonly TestType[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSystem, setIsSystem] = useState(false)
  // PLAN.md §7.6.24 Slice 4 -- recently-seen extra analytes on this panel's
  // orders, the operator's cue to add one as an optional member.
  const [extraAnalytes, setExtraAnalytes] = useState<readonly PanelExtraAnalyte[]>([])
  const archiveModal = useModal()
  const [archiving, setArchiving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    api
      .get<readonly TestType[]>('/test-types?isActive=true&limit=500')
      .then((res) => {
        if (cancelled) return
        if (res.data) setTestTypes(res.data)
      })
      .catch(() => {
        // non-fatal -- empty test type list just disables the picker
      })
    return () => {
      cancelled = true
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    if (isNew) {
      setForm(defaults)
      setError(null)
      setExtraAnalytes([])
      setIsSystem(false)
      return
    }
    if (!panelId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .get<Panel>(`/panels/${panelId}`)
      .then((res) => {
        if (cancelled) return
        if (res.data) {
          setForm(panelToForm(res.data))
          setIsSystem(res.data.isSystem)
        }
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load panel')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [panelId, isOpen, isNew])

  // PLAN.md §7.6.24 Slice 4 -- separate, non-blocking fetch: a failure here
  // just means no hint box, not a failure to load/edit the panel.
  useEffect(() => {
    if (!isOpen || isNew || !panelId) {
      setExtraAnalytes([])
      return
    }
    let cancelled = false
    api
      .get<readonly PanelExtraAnalyte[]>(`/panels/${panelId}/extra-analytes`)
      .then((res) => {
        if (cancelled) return
        setExtraAnalytes(res.data ?? [])
      })
      .catch(() => {
        if (!cancelled) setExtraAnalytes([])
      })
    return () => {
      cancelled = true
    }
  }, [panelId, isOpen, isNew])

  const update = <K extends keyof PanelForm>(key: K, value: PanelForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  function toggleTestType(id: string) {
    setForm((prev) => {
      if (prev.testTypeIds.includes(id)) {
        // Removing a member drops its memberConfig entry too -- keeps the
        // save payload consistent with the Zod subset rule (memberConfig
        // keys must be ⊆ testTypeIds).
        const { [id]: _removed, ...restMemberConfig } = prev.memberConfig
        return { ...prev, testTypeIds: prev.testTypeIds.filter((t) => t !== id), memberConfig: restMemberConfig }
      }
      return { ...prev, testTypeIds: [...prev.testTypeIds, id] }
    })
  }

  function updateMemberConfig(id: string, patch: Partial<MemberConfigEntry>) {
    setForm((prev) => {
      const current = prev.memberConfig[id] ?? defaultMemberConfig
      return { ...prev, memberConfig: { ...prev.memberConfig, [id]: { ...current, ...patch } } }
    })
  }

  function moveTestType(id: string, direction: -1 | 1) {
    setForm((prev) => {
      const idx = prev.testTypeIds.indexOf(id)
      if (idx === -1) return prev
      const newIdx = idx + direction
      if (newIdx < 0 || newIdx >= prev.testTypeIds.length) return prev
      const next = [...prev.testTypeIds]
      next.splice(idx, 1)
      next.splice(newIdx, 0, id)
      return { ...prev, testTypeIds: next }
    })
  }

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      let reflexRules: unknown[]
      try {
        reflexRules = JSON.parse(form.reflexRulesJson || '[]')
        if (!Array.isArray(reflexRules)) throw new Error('reflexRules must be a JSON array')
      } catch (err) {
        setError(`Reflex rules JSON invalid: ${err instanceof Error ? err.message : String(err)}`)
        setSaving(false)
        return
      }

      if (form.testTypeIds.length === 0) {
        setError('A panel must include at least one test type.')
        setSaving(false)
        return
      }

      const payload: Record<string, unknown> = {
        code: form.code,
        name: form.name,
        testTypeIds: form.testTypeIds,
        isReflexive: form.isReflexive,
        reflexRules,
      }
      if (form.description) payload.description = form.description
      if (Object.keys(form.memberConfig).length > 0) payload.memberConfig = form.memberConfig

      const res = isNew
        ? await api.post<Panel>('/panels', payload)
        : await api.put<Panel>(`/panels/${panelId}`, payload)
      if (!res.data) throw new Error('No data returned')
      onSaved(res.data)
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleArchive() {
    if (!panelId) return
    setArchiving(true)
    try {
      await api.delete(`/panels/${panelId}`)
      onArchived?.(panelId)
      archiveModal.close()
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to archive')
    } finally {
      setArchiving(false)
    }
  }

  const title = isNew ? 'New Panel' : `${form.code} - ${form.name}`
  const testTypeById = new Map(testTypes.map((tt) => [tt.id, tt]))

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={title} width="980px">
        {loading ? (
          <div style={{ color: colors.textMuted, padding: '1rem 0' }}>Loading...</div>
        ) : (
          <div>
            {isSystem && (
              <div
                style={{
                  padding: '0.625rem 0.875rem',
                  marginBottom: '1rem',
                  background: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  color: '#92400e',
                }}
                data-testid="system-record-notice"
              >
                This is a system-provided panel and cannot be edited or archived.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.75rem' }}>
              <FormField
                name="code"
                label="Code"
                required
                value={form.code}
                onChange={(v) => update('code', v)}
                disabled={isSystem}
              />
              <FormField
                name="name"
                label="Name"
                required
                value={form.name}
                onChange={(v) => update('name', v)}
                disabled={isSystem}
              />
            </div>

            <div style={{ marginTop: '0.75rem' }}>
              <FormField name="description" label="Description">
                <textarea
                  style={{ ...input, minHeight: '60px', resize: 'vertical' }}
                  value={form.description}
                  onChange={(e) => update('description', e.target.value)}
                />
              </FormField>
            </div>

            <div
              style={{
                borderTop: `1px solid ${colors.border}`,
                paddingTop: '0.75rem',
                marginTop: '0.75rem',
              }}
            >
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.9375rem', fontWeight: 600, color: colors.text }}>
                Test Types ({form.testTypeIds.length})
              </h3>
              <p style={{ margin: '0 0 0.5rem', fontSize: '0.8125rem', color: colors.textMuted }}>
                Order matters: tests appear on reports in this sequence. Use the arrows to reorder.
              </p>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.75rem',
                }}
              >
                <div>
                  <div style={{ fontSize: '0.8125rem', color: colors.textMuted, marginBottom: '0.25rem' }}>
                    Selected (in order)
                  </div>
                  <div
                    style={{
                      border: `1px solid ${colors.border}`,
                      borderRadius: '3px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      padding: '0.25rem',
                    }}
                  >
                    {form.testTypeIds.length === 0 ? (
                      <div style={{ color: colors.textMuted, padding: '0.5rem', fontSize: '0.875rem' }}>
                        No test types selected.
                      </div>
                    ) : (
                      form.testTypeIds.map((id, idx) => {
                        const tt = testTypeById.get(id)
                        const memberConfig = form.memberConfig[id] ?? defaultMemberConfig
                        return (
                          <div
                            key={id}
                            data-testid={`member-row-${id}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: '0.25rem',
                              padding: '0.25rem',
                              borderBottom: idx < form.testTypeIds.length - 1 ? `1px solid ${colors.border}` : 'none',
                              fontSize: '0.875rem',
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => moveTestType(id, -1)}
                              disabled={idx === 0}
                              style={{ ...btnSecondary, padding: '0.1rem 0.4rem', fontSize: '0.75rem' }}
                              title="Move up"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              onClick={() => moveTestType(id, 1)}
                              disabled={idx === form.testTypeIds.length - 1}
                              style={{ ...btnSecondary, padding: '0.1rem 0.4rem', fontSize: '0.75rem' }}
                              title="Move down"
                            >
                              ↓
                            </button>
                            <span style={{ flex: 1 }}>{tt ? `${tt.code} - ${tt.name}` : id.slice(0, 8)}</span>
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.2rem',
                                fontSize: '0.75rem',
                                color: colors.textMuted,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <input
                                type="checkbox"
                                data-testid={`member-required-${id}`}
                                checked={memberConfig.required}
                                onChange={(e) => updateMemberConfig(id, { required: e.target.checked })}
                              />
                              Required
                            </label>
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.2rem',
                                fontSize: '0.75rem',
                                color: colors.textMuted,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <input
                                type="checkbox"
                                data-testid={`member-include-in-report-${id}`}
                                checked={memberConfig.includeInReport}
                                onChange={(e) => updateMemberConfig(id, { includeInReport: e.target.checked })}
                              />
                              Include in report
                            </label>
                            <button
                              type="button"
                              onClick={() => toggleTestType(id)}
                              style={{
                                ...btnSecondary,
                                padding: '0.1rem 0.4rem',
                                fontSize: '0.75rem',
                                color: colors.danger,
                              }}
                              title="Remove"
                            >
                              ×
                            </button>
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.8125rem', color: colors.textMuted, marginBottom: '0.25rem' }}>
                    Available
                  </div>
                  <div
                    style={{
                      border: `1px solid ${colors.border}`,
                      borderRadius: '3px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      padding: '0.25rem',
                    }}
                  >
                    {testTypes
                      .filter((tt) => !form.testTypeIds.includes(tt.id))
                      .map((tt, idx, arr) => (
                        <div
                          key={tt.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0.25rem',
                            borderBottom: idx < arr.length - 1 ? `1px solid ${colors.border}` : 'none',
                            fontSize: '0.875rem',
                          }}
                        >
                          <span>
                            {tt.code} - {tt.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => toggleTestType(tt.id)}
                            style={{ ...btnSecondary, padding: '0.1rem 0.4rem', fontSize: '0.75rem' }}
                          >
                            +
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>

            {extraAnalytes.length > 0 && (
              <div
                data-testid="extra-analytes-hint"
                style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem',
                  background: colors.bgMuted,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '3px',
                  fontSize: '0.8125rem',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '0.4rem', color: colors.text }}>
                  Extra analytes seen recently from instruments on this panel's orders:
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {extraAnalytes.map((a) => (
                      <tr key={a.testCode ?? a.testName ?? Math.random()}>
                        <td style={{ padding: '0.15rem 0.5rem 0.15rem 0', color: colors.text }}>
                          {a.testCode ?? '--'}
                          {a.testName ? ` - ${a.testName}` : ''}
                        </td>
                        <td style={{ padding: '0.15rem 0.5rem', color: colors.textMuted }}>
                          {a.count} occurrence{a.count === 1 ? '' : 's'}
                        </td>
                        <td style={{ padding: '0.15rem 0', color: colors.textMuted }}>
                          last seen {new Date(a.lastSeenAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ marginTop: '0.4rem', color: colors.textMuted }}>
                  Add one as an optional member above to start tracking it.
                </div>
              </div>
            )}

            <div
              style={{
                borderTop: `1px solid ${colors.border}`,
                paddingTop: '0.75rem',
                marginTop: '0.75rem',
              }}
            >
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9375rem' }}>
                <input
                  type="checkbox"
                  checked={form.isReflexive}
                  onChange={(e) => update('isReflexive', e.target.checked)}
                />
                Reflexive panel (rules below trigger follow-up tests)
              </label>
              {form.isReflexive && (
                <div style={{ marginTop: '0.5rem' }}>
                  <FormField name="reflexRulesJson" label="Reflex Rules (JSON)">
                    <textarea
                      style={{
                        ...input,
                        minHeight: '120px',
                        resize: 'vertical',
                        fontFamily: 'monospace',
                        fontSize: '0.8125rem',
                      }}
                      value={form.reflexRulesJson}
                      onChange={(e) => update('reflexRulesJson', e.target.value)}
                      placeholder='[{"when": {"abnormalFlag": "HH"}, "addTestTypeIds": ["..."]}]'
                    />
                  </FormField>
                </div>
              )}
            </div>

            {error && (
              <div
                style={{
                  marginTop: '0.75rem',
                  color: colors.danger,
                  padding: '0.5rem 0.75rem',
                  background: colors.bgError,
                  border: `1px solid ${colors.borderError}`,
                  borderRadius: '3px',
                  fontSize: '0.875rem',
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
              {!isNew && !isSystem && onArchived && (
                <button
                  type="button"
                  onClick={archiveModal.open}
                  style={{ ...btnDanger, marginRight: 'auto' }}
                  disabled={saving || archiving}
                >
                  Archive
                </button>
              )}
              <button type="button" onClick={onClose} style={btnSecondary} disabled={saving}>
                {isSystem ? 'Close' : 'Cancel'}
              </button>
              {!isSystem && (
                <button type="button" onClick={handleSave} style={btnPrimary} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive Panel"
        message={`Archive ${form.name}? It will be hidden from new orders but historical orders are preserved.`}
        confirmLabel="Archive"
        loading={archiving}
      />
    </>
  )
}
